import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FilterCheckbox from './FilterCheckbox';
import { SPLIT_TYPES } from './types';
import type { OperationColumnFilters, SplitType, StatusFlag } from './types';

interface StatusFilterContentProps {
  value: OperationColumnFilters['status'];
  onChange: (next: OperationColumnFilters['status']) => void;
  show?: {
    coupleIncome?: boolean;
    sharedExpense?: boolean;
    splitTypes?: boolean;
  };
  splitTypeLabels: Record<SplitType, string>;
}

// Bascule générique d'une valeur dans un tableau.
function toggle<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

export default function StatusFilterContent({
  value,
  onChange,
  show = {},
  splitTypeLabels,
}: StatusFilterContentProps) {
  const { t } = useTranslation('operations');
  const [splitOpen, setSplitOpen] = useState(value.splitTypes.length > 0);

  const toggleValidation = (v: 'validated' | 'unvalidated') =>
    onChange({ ...value, validation: toggle(value.validation, v) });
  const toggleRecurrence = (v: 'recurring' | 'oneoff') =>
    onChange({ ...value, recurrence: toggle(value.recurrence, v) });
  const toggleFlag = (v: StatusFlag) =>
    onChange({ ...value, flags: toggle(value.flags, v) });
  const toggleSplit = (v: SplitType) =>
    onChange({ ...value, splitTypes: toggle(value.splitTypes, v) });

  return (
    <div className="space-y-2 min-w-[200px]">
      {/* Validation */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-0.5">{t('filters.validation')}</p>
        <FilterCheckbox
          checked={value.validation.includes('validated')}
          onChange={() => toggleValidation('validated')}
          label={t('filters.validated')}
        />
        <FilterCheckbox
          checked={value.validation.includes('unvalidated')}
          onChange={() => toggleValidation('unvalidated')}
          label={t('filters.unvalidated')}
        />
      </div>

      {/* Récurrence */}
      <div className="pt-1 border-t border-gray-100">
        <p className="text-xs font-medium text-gray-500 mb-0.5">{t('filters.recurrence')}</p>
        <FilterCheckbox
          checked={value.recurrence.includes('recurring')}
          onChange={() => toggleRecurrence('recurring')}
          label={t('filters.recurring')}
        />
        <FilterCheckbox
          checked={value.recurrence.includes('oneoff')}
          onChange={() => toggleRecurrence('oneoff')}
          label={t('filters.oneoff')}
        />
      </div>

      {/* Drapeaux divers */}
      <div className="pt-1 border-t border-gray-100">
        <FilterCheckbox
          checked={value.flags.includes('internalTransfer')}
          onChange={() => toggleFlag('internalTransfer')}
          label={t('filters.internalTransfer')}
        />
        {show.coupleIncome && (
          <FilterCheckbox
            checked={value.flags.includes('coupleIncome')}
            onChange={() => toggleFlag('coupleIncome')}
            label={t('filters.coupleIncome')}
          />
        )}
        {show.sharedExpense && (
          <FilterCheckbox
            checked={value.flags.includes('sharedExpense')}
            onChange={() => toggleFlag('sharedExpense')}
            label={t('filters.sharedExpense')}
          />
        )}
      </div>

      {/* Répartition (groupe dépliable) */}
      {show.splitTypes && (
        <div className="pt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setSplitOpen((o) => !o)}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 w-full"
          >
            {splitOpen ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
            {t('filters.splitType')}
            {value.splitTypes.length > 0 && (
              <span className="ml-1 text-primary-600">({value.splitTypes.length})</span>
            )}
          </button>
          {splitOpen && (
            <div className="pl-4 mt-0.5">
              {SPLIT_TYPES.map((st) => (
                <FilterCheckbox
                  key={st}
                  checked={value.splitTypes.includes(st)}
                  onChange={() => toggleSplit(st)}
                  label={splitTypeLabels[st]}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
